// @flow

import * as React from 'react';
import { Flex, Box, useSystem } from 'react-system';
import Divider from '@material-ui/core/Divider';
import { graphql, useFragment } from 'react-relay';
import { compareAsc, format, parseISO } from 'date-fns';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import type { AggregatesListingPropertyDetails_listing$key } from './__generated__/AggregatesListingPropertyDetails_listing.graphql';

type Props = {|
  listing: AggregatesListingPropertyDetails_listing$key,
  propertyMainType?: string,
|};

export const AggregatesListingPropertyDetails = (props: Props): React.Node => {
  const { text } = useTheme();
  const { media } = useSystem();
  const { t, numberLocale } = useLocale();
  const formatOptions = {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  };

  const listing = useFragment(
    graphql`
      fragment AggregatesListingPropertyDetails_listing on AggregatesListing {
        livingSurface
        usableSurface
        gardenSurface
        terraceSurface
        balconySurface
        landSurface
        numberOfRooms
        numberOfBedrooms
        numberOfBathrooms
        availableFrom
        constructionYear
        renovationYear
        floorOfFlat
        numberOfLevels
        volume
        numberOfIndoorParkings
        numberOfOutdoorParkings
        numberOfToilets
        url {
          originalDomain
        }
      }
    `,
    props.listing,
  );

  const {
    availableFrom,
    constructionYear,
    renovationYear,
    livingSurface,
    usableSurface,
    gardenSurface,
    terraceSurface,
    balconySurface,
    landSurface,
    volume,

    numberOfRooms,
    numberOfBedrooms,
    numberOfBathrooms,
    floorOfFlat,
    numberOfLevels,
    numberOfIndoorParkings,
    numberOfOutdoorParkings,
    numberOfToilets,
  } = listing;

  const availableFromDate =
    availableFrom != null ? parseISO(availableFrom) : null;
  const isAvailableImmediately = availableFromDate
    ? compareAsc(new Date(), availableFromDate) >= 0
    : false;

  const years = [
    { label: t('constructionYear'), value: constructionYear },
    { label: t('renovationYear'), value: renovationYear },
  ].filter(item => item.value != null);

  const surfaces = [
    { label: t('livingSurface'), value: livingSurface, unit: 'm²' },
    { label: t('usableSurface'), value: usableSurface, unit: 'm²' },
    { label: t('gardenSurface'), value: gardenSurface, unit: 'm²' },
    { label: t('terraceSurface'), value: terraceSurface, unit: 'm²' },
    { label: t('balconySurface'), value: balconySurface, unit: 'm²' },
    { label: t('landSurface'), value: landSurface, unit: 'm²' },
    { label: t('buildingVolume'), value: volume, unit: 'm³' },
  ].filter(item => item.value != null);

  const configuration = [
    { label: t('numberOfRooms'), value: numberOfRooms },
    { label: t('numberOfBedrooms'), value: numberOfBedrooms },
    { label: t('numberOfBathrooms'), value: numberOfBathrooms },
    { label: t('numberOfToilets'), value: numberOfToilets },
    { label: t('floorOfFlat'), value: floorOfFlat },
    { label: t('numberOfLevels'), value: numberOfLevels },
  ].filter(item => item.value != null);

  const parkingSpaces = [
    {
      label: t('numberOfIndoorParkings'),
      value: numberOfIndoorParkings,
    },
    {
      label: t('numberOfOutdoorParkings'),
      value: numberOfOutdoorParkings,
    },
  ].filter(item => item.value != null);

  const displayPropertyDetails =
    props.propertyMainType === 'PROP'
      ? surfaces.length > 0 || parkingSpaces.length > 0
      : surfaces.length > 0 ||
        configuration.length > 0 ||
        parkingSpaces.length > 0;

  return (
    displayPropertyDetails && (
      <Box pr={[0, 2]} mb={4}>
        <Box as="h3" py={2} css={media([text.h6, text.h5])}>
          {t('propertyDetails')}
        </Box>
        <Flex flexWrap="wrap" mx={-3}>
          {surfaces.length > 0 && (
            <Box px={3} width={[1, 1 / 2]} css={media({ minWidth: ['280px'] })}>
              {listing.url?.originalDomain.includes('realadvisor.ch') && (
                <React.Fragment>
                  <Flex py={[2, 2, 3]} flexWrap="wrap">
                    <Box flexGrow={1} css={text.subtitle1}>
                      {t('availableFrom')}
                    </Box>
                    <Box
                      flexShrink={0}
                      css={[text.body1, { textAlign: 'right' }]}
                    >
                      {availableFromDate
                        ? isAvailableImmediately
                          ? t('immediately')
                          : format(availableFromDate, 'dd.MM.yyyy')
                        : t('byAgreement')}
                    </Box>
                  </Flex>
                  <Divider />
                </React.Fragment>
              )}
              {years.map(y => (
                <React.Fragment key={y.label}>
                  <Flex py={[2, 2, 3]} flexWrap="wrap">
                    <Box flexGrow={1} css={text.subtitle1}>
                      {y.label}
                    </Box>
                    <Box
                      flexShrink={0}
                      css={[text.body1, { textAlign: 'right' }]}
                    >
                      {y.value ?? ''}
                    </Box>
                  </Flex>
                  <Divider />
                </React.Fragment>
              ))}
              {surfaces.map(surface => (
                <React.Fragment key={surface.label}>
                  <Flex py={[2, 2, 3]} flexWrap="wrap">
                    <Box flexGrow={1} css={text.subtitle1}>
                      {surface.label}
                    </Box>
                    <Box
                      flexShrink={0}
                      css={[text.body1, { textAlign: 'right' }]}
                    >
                      {`${
                        surface.value != null
                          ? numberLocale.format(surface.value, formatOptions)
                          : ''
                      } ${surface.unit}`}
                    </Box>
                  </Flex>
                  <Divider />
                </React.Fragment>
              ))}
            </Box>
          )}
          {props.propertyMainType !== 'PROP' && (
            <Box px={3} width={[1, 1 / 2]} css={media({ minWidth: ['280px'] })}>
              {configuration.map(config => (
                <React.Fragment key={config.label}>
                  <Flex py={[2, 2, 3]} flexWrap="wrap">
                    <Box flexGrow={1} css={text.subtitle1}>
                      {config.label}
                    </Box>
                    <Box
                      flexShrink={0}
                      css={[text.body1, { textAlign: 'right' }]}
                    >
                      {config.value}
                    </Box>
                  </Flex>
                  <Divider />
                </React.Fragment>
              ))}

              {parkingSpaces.map(config => (
                <React.Fragment key={config.label}>
                  <Flex py={[2, 2, 3]} flexWrap="wrap">
                    <Box flexGrow={1} css={text.subtitle1}>
                      {config.label}
                    </Box>
                    <Box
                      flexShrink={0}
                      css={[text.body1, { textAlign: 'right' }]}
                    >
                      {config.value}
                    </Box>
                  </Flex>
                  <Divider />
                </React.Fragment>
              ))}
            </Box>
          )}
        </Flex>
      </Box>
    )
  );
};
